import Vue from "vue";
import Vuex from "vuex";
import { FormatInfo, formatValue2, formatValue } from '../utils';

Vue.use(Vuex);

export default new Vuex.Store({
	state: {
		onMobile: false,
		grossIncome: 0,
		grossIncomeFreeze: 0,
		taxableIncome: 0,
		allowanceBase: 12500,
		allowanceAdjusted: 0,
		totalTax: 0,
		totalIncomeTax: 0,
		totalNI: 0,
		taxAt20: 0,
		taxAt40: 0,
		taxAt45: 0,
		nationalInsurance12: 0,
		nationalInsurance2: 0,
		takeHome: 0,
		isScottish: 0,
		selectedRange: [20, 70],
		useTabs: true,
		stepSize: "500",
		//ranges: ["0 - 200", "0 - 40", "20 - 70", "50 - 150"]
	},
	mutations: {
		SET_ON_MOBILE(state, onmobile) {
			state.onMobile = onmobile;
		},
		SET_STEP_SIZE(state, stepSize) {
			state.stepSize = stepSize;
		},
		SET_USE_TABS(state, useTabs) {
			state.useTabs = useTabs;
		},
		SET_GROSS_INCOME(state, gross) {
			state.grossIncome = gross;
		},
		SET_GROSS_INCOME_FREEZE(state) {
			state.grossIncomeFreeze = state.grossIncome;
		},
		SET_GROSS_INCOME_UNFREEZE(state) {
			state.grossIncome = state.grossIncomeFreeze;
			state.grossIncomeFreeze = 0;
		},
		SET_SELECTED_RANGE(state, range) {
			if (state.selectedRange.toString() == range.toString()) return;
			state.selectedRange = range;
		},
	},
	actions: {
		setGrossIncome({ state, commit }, gross) {
			gross = Number(gross);
			//console.log("*** store - setGrossIncome " + gross);

			let min = state.selectedRange[0];
			let max = state.selectedRange[1];

			let grossInK = gross / 1000;

			min = getLowerBound(min, grossInK);
			max = getUpperBound(max, grossInK);

			commit("SET_SELECTED_RANGE", [min, max]);

			commit("SET_GROSS_INCOME", gross);
			//
			calcTaxes(state, gross);
		},
		setTakeHome({ state, commit, dispatch, getters }, reqTakehome) {
			reqTakehome = Number(reqTakehome);

			let x = 0;
			let jumpBy = 10000;
			let goingUp = true;
			let reverse = false;
			let margin = 0.4;	// by 1 pound

			while (true) {
				// try out various entries and home in up/down until we hit the takehome target
				dispatch("setGrossIncome", x);
				let temptakehome = getters.getTakeHome("monthly", 0, false);

				// bit wide but try
				if (reqTakehome >= temptakehome - margin && reqTakehome <= temptakehome + margin) {
					commit("SET_STEP_SIZE", "1");
					return;
				}

				if (temptakehome < reqTakehome) {
					if (!goingUp) reverse = true;
					goingUp = true;
				}
				else if (temptakehome > reqTakehome) {
					if (goingUp) reverse = true;
					goingUp = false;
				}

				// if we switched direction then decrease the jump size
				if (reverse) {
					reverse = false;
					if (jumpBy == 10000) jumpBy = 1000;
					else if (jumpBy == 1000) jumpBy = 100;
					else if (jumpBy == 100) jumpBy = 10;
					else if (jumpBy == 10) jumpBy = 2;
					else if (jumpBy == 2) jumpBy = 0.4;
				}

				goingUp ? (x += jumpBy) : x -= jumpBy;
				if (x < 0) x = 0;

			}
		},
		freezeGrossincome({ commit }) {
			commit("SET_GROSS_INCOME_FREEZE");
		},
		unfreezeGrossincome({ state, commit, dispatch }) {
			commit("SET_GROSS_INCOME_UNFREEZE");
			dispatch("setGrossIncome", state.grossIncome);
		},
		setSelectedRange({ state, commit }, range) {
			console.log("*** setSelectedRange");
			commit("SET_SELECTED_RANGE", range);
		},
		setUseTabs({ state, commit }, useTabs) {
			commit("SET_USE_TABS", useTabs);
		},
		setStepSize({ state, commit }, stepSize) {
			commit("SET_STEP_SIZE", stepSize);
		},
	},
	getters: {
		onMobile(state) {
			return state.onMobile;
		},
		useTabs(state) {
			return state.useTabs;
		},
		stepSize(state) {
			return state.stepSize;
		},
		selectedRange(state) {
			return state.selectedRange;
		},
		getRangeMin(state) {
			return state.selectedRange[0];
		},
		getRangeMax(state) {
			return state.selectedRange[1];
		},
		getData(state) {
			return (formatInfo, property) => formatValue2(formatInfo, state[property]);
		},
		getGrossIncome(state) {
			return (period, decimals = 2, format = true, signMode = "normal") => formatValue(period, state.grossIncome, decimals, format, signMode);
		},
		getTaxableIncome(state) {
			return (period, decimals = 2, format = true, signMode = "normal") => formatValue(period, state.taxableIncome, decimals, format, signMode);
		},
		getTax(state) {
			return (period, decimals = 2, format = true, signMode = "normal") => formatValue(period, state.totalTax, decimals, format, signMode);
		},
		getIncomeTax(state) {
			return (period, decimals = 2, format = true, signMode = "normal") => formatValue(period, state.totalIncomeTax, decimals, format, signMode);
		},
		getNationalInsurance(state) {
			return (period, decimals = 2, format = true, signMode = "normal") => formatValue(period, state.totalNI, decimals, format, signMode);
		},
		getTakeHome(state) {
			return (period, decimals = 2, format = true, signMode = "normal") => formatValue(period, state.takeHome, decimals, format, signMode);
		},
		getAllowanceBase(state) {
			return state.allowanceBase;
		},
		getAllowanceAdjusted(state) {
			return state.allowanceAdjusted;
		},
		// returns an array in this order!
		getChartData(state, getters) {
			return [
				state.grossIncome,
				state.taxableIncome,
				state.totalTax,
				state.totalIncomeTax,
				state.totalNI,
				state.takeHome
			];
		}
	}
});

function calcTaxes(store, gross) {

	let allowance = store.allowanceBase;

	// normal cases 
	store.taxableIncome = gross > allowance ? gross - allowance : 0;

	// if gross > 100k then reduce the allowance
	if (gross > 100000) {
		let excess = gross - 100000;
		allowance -= excess / 2;
		if (allowance < 0) allowance = 0;

		store.allowanceAdjusted = allowance;
		store.taxableIncome = gross - store.allowanceAdjusted;
	}

	var { tax, remainder } = calcBandTax(store, allowance, gross, 0);
	var { tax, remainder } = calcBandTax(store, (37500 - 0), remainder, 20);
	store.taxAt20 = tax;
	var { tax, remainder } = calcBandTax(store, (150000 - 37500), remainder, 40);
	store.taxAt40 = tax;
	var { tax, remainder } = calcBandTax(store, (1000000), remainder, 45);
	store.taxAt45 = tax;
	store.totalIncomeTax = store.taxAt20 + store.taxAt40 + store.taxAt45;

	// 
	var { ni, remainder } = calcBandNI(store, 9500, gross, 0);
	var { ni, remainder } = calcBandNI(store, (50000 - 9500), remainder, 12);
	store.nationalInsurance12 = ni;
	store.nationalInsurance2 = remainder * 0.02;
	store.totalNI = store.nationalInsurance12 + store.nationalInsurance2;

	// calc the real total tax (income + ni)
	store.totalTax = store.totalIncomeTax + store.totalNI;

	// now calc takehome
	store.takeHome = gross - store.totalTax;

}

function calcBandTax(store, bandAmount, remainderAmount, taxRate) {
	let taxableAmount = remainderAmount > bandAmount ? bandAmount : remainderAmount;
	let tax = taxableAmount * (taxRate / 100);
	let remainder = remainderAmount - taxableAmount;
	if (remainder < 0) remainder = 0;
	return { tax, remainder };
}

function calcBandNI(store, bandAmount, remainderAmount, taxRate) {
	let taxableAmount = remainderAmount > bandAmount ? bandAmount : remainderAmount;
	let ni = taxableAmount * (taxRate / 100);
	let remainder = remainderAmount - taxableAmount;
	if (remainder < 0) remainder = 0;
	return { ni, remainder };
}

// function formatValue(period, amount, decimals = 2, format = true) {
// 	if (period == "monthly") amount /= 12;
// 	if (period == "weekly") amount /= 52;
// 	if (period == "daily") amount /= (52 * 5);

// 	if (!format) return amount;

// 	const options = {
// 		minimumFractionDigits: decimals,
// 		maximumFractionDigits: decimals
// 	};
// 	const formatted = Number(amount).toLocaleString('en', options);

// 	return "£ " + formatted;
// }

function getLowerBound(rangeMin, x) {
	if (rangeMin <= x) return rangeMin;
	rangeMin -= 10;
	return getLowerBound(rangeMin, x);
}

function getUpperBound(rangeMax, x) {
	if (rangeMax >= x) return rangeMax;
	rangeMax += 10;
	return getUpperBound(rangeMax, x);
}